/**
 * webm-fix-duration.js — minimal EBML rewriter that injects a Segment
 * Duration into a WebM Blob produced by MediaRecorder.
 *
 * Why this exists
 * ───────────────
 * Chromium's MediaRecorder does not finalize the EBML Segment with a
 * Duration element when it stops (Chromium bug 642012, open since 2016).
 * The file plays in modern web players because they tolerate "live"
 * streams, but desktop players (Windows 11 "Movies & TV", VLC < 3.0.18,
 * QuickTime, every old reader) report duration 0:00 / "broken file" /
 * fail to seek. This is the most-cited "битый файл" cause for
 * MediaRecorder-WebM output.
 *
 * Strategy
 * ────────
 * 1. Probe the actual playback duration via a hidden <video> element.
 *    For WebM-from-MediaRecorder this reads as Infinity initially, so we
 *    use the well-known "currentTime = MAX_SAFE_INTEGER → timeupdate →
 *    duration" trick.
 * 2. Walk the EBML tree to locate Segment → Info → Duration.
 *    - If Duration exists (rare for MediaRecorder output): overwrite
 *      its value bytes in place. Same byte-length, no resize.
 *    - If Duration is missing (the common case): insert it inside Info,
 *      then resize Info, Segment, and patch their VINT-encoded sizes.
 * 3. Return a NEW Blob. The original blob is never mutated.
 *
 * Element IDs (WebM / EBML, Matroska v4):
 *   Segment  0x18538067
 *   Info     0x1549A966
 *   Duration 0x4489    (encoded as 0x4489 — 2-byte ID)
 *
 * VINT (variable-length integer) encoding: leading 1 bits indicate the
 * total byte length. e.g. 0x80=1 byte, 0x4xxx=2 bytes, 0x2xxxxx=3 bytes,
 * 0x1xxxxxxx=4 bytes, etc. Special "all bytes 0xff" within the size
 * field encodes UNKNOWN — MediaRecorder uses this for the Segment.
 *
 * No external deps. ~200 LOC.
 */
(function () {
'use strict';
if (window.WebmFixDuration) return;

// Element IDs we care about (raw, not VINT-encoded — same byte sequence).
var ID_SEGMENT  = 0x18538067;
var ID_INFO     = 0x1549A966;
var ID_DURATION = 0x4489;

function readVint(buf, off) {
    var first = buf[off];
    if (first === 0) throw new Error('Invalid VINT at ' + off);
    // Find leading-1-bit position; that determines the length (1..8 bytes).
    var len = 0, mask = 0x80;
    while ((first & mask) === 0) { len++; mask >>= 1; }
    len++;
    if (off + len > buf.length) throw new Error('VINT past end');
    // Strip the marker bit, read the rest as big-endian.
    var value = first & (mask - 1);
    for (var i = 1; i < len; i++) value = value * 256 + buf[off + i];
    return { value: value, length: len };
}

function readElementId(buf, off) {
    // Element IDs are VINTs but the leading-bit marker is preserved.
    var first = buf[off];
    var len = 0, mask = 0x80;
    while ((first & mask) === 0) { len++; mask >>= 1; }
    len++;
    var id = 0;
    for (var i = 0; i < len; i++) id = id * 256 + buf[off + i];
    return { id: id, length: len };
}

// Encode a number as a VINT of the smallest length that fits it.
function encodeVint(value) {
    var len = 1;
    while (value >= (1 << (7 * len)) - 1 && len < 8) len++;
    var out = new Uint8Array(len);
    var mask = 1 << (8 - len);
    var v = value;
    for (var i = len - 1; i > 0; i--) { out[i] = v & 0xff; v = Math.floor(v / 256); }
    out[0] = mask | (v & 0xff);
    return out;
}

// Encode 8-byte big-endian float64.
function encodeFloat64(value) {
    var dv = new DataView(new ArrayBuffer(8));
    dv.setFloat64(0, value, false);
    return new Uint8Array(dv.buffer);
}

// Walk the children of an element body, calling visit(id, idLen, sizeLen, valueOff, valueLen)
// for each child. Stops at end of body.
function walkChildren(buf, bodyStart, bodyEnd, visit) {
    var off = bodyStart;
    while (off < bodyEnd) {
        var idR = readElementId(buf, off);
        var sizeR = readVint(buf, off + idR.length);
        var valueOff = off + idR.length + sizeR.length;
        // UNKNOWN size sentinel: all marker bits + payload bits = 1
        // For VINT length L, that's (1<<(7*L))-1.
        var unknown = sizeR.value === Math.pow(2, 7 * sizeR.length) - 1;
        var valueLen = unknown ? bodyEnd - valueOff : sizeR.value;
        visit(idR.id, idR.length, sizeR.length, valueOff, valueLen, unknown);
        off = valueOff + valueLen;
    }
}

// Probe real duration of a Blob by loading it in a hidden <video> element
// and using the currentTime-overshoot trick to force a real metadata scan.
// Returns duration in seconds, or 0 if probe fails.
function probeDuration(blob) {
    return new Promise(function (resolve) {
        var v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        v.playsInline = true;
        var url = URL.createObjectURL(blob);
        var done = false;
        var timeout = setTimeout(function () { finish(0); }, 8000);

        function finish(d) {
            if (done) return;
            done = true;
            clearTimeout(timeout);
            try { URL.revokeObjectURL(url); } catch (_) {}
            try { v.src = ''; v.load(); } catch (_) {}
            resolve(isFinite(d) && d > 0 ? d : 0);
        }

        v.onerror = function () { finish(0); };
        v.onloadedmetadata = function () {
            if (isFinite(v.duration) && v.duration > 0) {
                finish(v.duration);
                return;
            }
            // Force seek past end → browser scans for last cluster timestamp.
            v.currentTime = Number.MAX_SAFE_INTEGER;
            v.ontimeupdate = function () {
                v.ontimeupdate = null;
                finish(v.duration);
            };
        };
        v.src = url;
    });
}

/**
 * Patch a WebM Blob to include Segment Duration metadata.
 *
 * @param {Blob} blob — WebM blob from MediaRecorder.
 * @param {number} [knownDurationSec] — pass if you already measured it
 *   (e.g., from your own timer). If omitted, probes via <video>.
 * @returns {Promise<Blob>} new blob with Duration patched, or original
 *   if patching fails (file is still playable, just imperfect metadata).
 */
async function patch(blob, knownDurationSec) {
    try {
        if (!blob || blob.size < 64) return blob;
        var dur = (knownDurationSec && knownDurationSec > 0)
            ? knownDurationSec
            : await probeDuration(blob);
        if (!dur) return blob;

        var buf = new Uint8Array(await blob.arrayBuffer());

        // Find Segment element. Skip EBML header (id 0x1A45DFA3) at start.
        var off = 0;
        var segmentStart = -1, segmentBodyStart = -1, segmentBodyEnd = -1, segmentSizeOff = -1, segmentSizeLen = -1;
        while (off < buf.length - 12) {
            var idR = readElementId(buf, off);
            var sizeR = readVint(buf, off + idR.length);
            var valueOff = off + idR.length + sizeR.length;
            var unknown = sizeR.value === Math.pow(2, 7 * sizeR.length) - 1;
            var valueLen = unknown ? buf.length - valueOff : sizeR.value;

            if (idR.id === ID_SEGMENT) {
                segmentStart = off;
                segmentSizeOff = off + idR.length;
                segmentSizeLen = sizeR.length;
                segmentBodyStart = valueOff;
                segmentBodyEnd = valueOff + valueLen;
                break;
            }
            off = valueOff + valueLen;
        }
        if (segmentStart < 0) return blob;

        // Locate Info element and Duration child within it.
        var infoStart = -1, infoBodyStart = -1, infoBodyEnd = -1, infoSizeOff = -1, infoSizeLen = -1;
        var durationOff = -1, durationLen = -1;

        walkChildren(buf, segmentBodyStart, segmentBodyEnd, function (id, idLen, sizeLen, valOff, valLen, unk) {
            if (id === ID_INFO) {
                infoStart = valOff - idLen - sizeLen;
                infoSizeOff = valOff - sizeLen;
                infoSizeLen = sizeLen;
                infoBodyStart = valOff;
                infoBodyEnd = valOff + valLen;
                walkChildren(buf, infoBodyStart, infoBodyEnd, function (id2, idLen2, sizeLen2, valOff2, valLen2) {
                    if (id2 === ID_DURATION) {
                        durationOff = valOff2;
                        durationLen = valLen2;
                    }
                });
            }
        });
        if (infoStart < 0) return blob;

        // Segment Info uses TimecodeScale (default 1_000_000 = 1ms units).
        // Duration is a float in TimecodeScale units. We assume default scale;
        // actual scale is also in Info but real-world MR output uses 1_000_000.
        // Read TimecodeScale if present.
        var timecodeScale = 1000000;
        walkChildren(buf, infoBodyStart, infoBodyEnd, function (id, idLen, sizeLen, valOff, valLen) {
            if (id === 0x2AD7B1) {
                var v = 0;
                for (var i = 0; i < valLen; i++) v = v * 256 + buf[valOff + i];
                if (v > 0) timecodeScale = v;
            }
        });

        var durationValue = (dur * 1e9) / timecodeScale; // seconds → scale-units

        // Case 1: Duration exists with float64 (8 bytes) — overwrite in place.
        if (durationOff >= 0 && durationLen === 8) {
            var newBuf = new Uint8Array(buf);
            var floatBytes = encodeFloat64(durationValue);
            newBuf.set(floatBytes, durationOff);
            return new Blob([newBuf], { type: blob.type });
        }

        // Case 2: insert a new Duration element inside Info.
        // Element layout: [ID:2 bytes][Size:1 byte VINT 0x88][Value:8 bytes]
        // = 11 bytes total. Append at end of Info body for simplicity.
        var durElement = new Uint8Array(11);
        durElement[0] = 0x44; durElement[1] = 0x89;     // ID
        durElement[2] = 0x88;                            // size = 8 (VINT 1-byte)
        var floatBytes2 = encodeFloat64(durationValue);
        durElement.set(floatBytes2, 3);

        var inserted = durElement.length;

        // New Info size = old + 11. Re-encode VINT, may grow by 0..N bytes.
        var newInfoBodyLen = (infoBodyEnd - infoBodyStart) + inserted;
        var newInfoSizeBytes = encodeVint(newInfoBodyLen);
        var infoSizeDelta = newInfoSizeBytes.length - infoSizeLen;

        // If Segment had a known size, it grows too.
        var segmentSizeUnknown = (function () {
            var v = 0;
            for (var i = 0; i < segmentSizeLen; i++) v = v * 256 + buf[segmentSizeOff + i];
            // remove the marker bit on first byte
            var firstByte = buf[segmentSizeOff];
            var maskBit = 0x80;
            while ((firstByte & maskBit) === 0) maskBit >>= 1;
            v &= ~(maskBit << (8 * (segmentSizeLen - 1)));
            // Mask of all 1s in payload?
            var maxPayload = Math.pow(2, 7 * segmentSizeLen) - 1;
            return v === maxPayload;
        })();

        var segmentSizeBytes = null, segmentSizeDelta = 0;
        if (!segmentSizeUnknown) {
            var newSegmentBodyLen = (segmentBodyEnd - segmentBodyStart) + inserted + infoSizeDelta;
            segmentSizeBytes = encodeVint(newSegmentBodyLen);
            segmentSizeDelta = segmentSizeBytes.length - segmentSizeLen;
        }

        // Assemble new buffer: [..Segment header..][..before Info size..][newInfoSize][..Info body..][durElement][..after Info..]
        // For simplicity, build it as a sequence of slices.
        var parts = [];
        // 0..segmentSizeOff: bytes before Segment size (Segment ID)
        parts.push(buf.subarray(0, segmentSizeOff));
        // Segment size (possibly resized) or original.
        if (segmentSizeBytes) parts.push(segmentSizeBytes);
        else parts.push(buf.subarray(segmentSizeOff, segmentSizeOff + segmentSizeLen));
        // segmentBodyStart..infoSizeOff (any siblings before Info, plus Info ID)
        parts.push(buf.subarray(segmentBodyStart, infoSizeOff));
        // New Info size
        parts.push(newInfoSizeBytes);
        // Info body
        parts.push(buf.subarray(infoBodyStart, infoBodyEnd));
        // New Duration element
        parts.push(durElement);
        // After Info — rest of file
        parts.push(buf.subarray(infoBodyEnd));

        return new Blob(parts, { type: blob.type });
    } catch (e) {
        try { console.warn('[webm-fix-duration] patch failed', e); } catch (_) {}
        return blob;
    }
}

window.WebmFixDuration = { patch: patch, probeDuration: probeDuration };

})();
